import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { GlobalContext } from '../../GlobalContext'

// Ephemeral, pre-database activity hints sent to other apps in the same organisation.
// See websocket/activity_broadcaster_signals.md. Best-effort: a failure here must
// never disrupt answering.

export type ActivityEvent =
  | 'isAnsweringChecklist'
  | 'noLongerAnsweringChecklist'
  | 'isAnsweringQuestion'
  | 'isTakingPhoto'
  | 'noLongerTakingPhoto'
  | 'isScanningQRCode'
  | 'noLongerScanningQRCode'
  | 'isEnteringReading'
  | 'noLongerEnteringReading'
  | 'hasCapturedPhoto'
  | 'isTyping'
  | 'hasChosenOption'
  | 'isDrawingSignature'
  | 'isSpeakingAnswer'
  | 'noLongerSpeakingAnswer'
  | 'isAdjustingLocation'
  | 'isAddingNote'
  | 'noLongerAddingNote'
  | 'isSavingAnswer'

type Scope = { checkPeriodId?: string | null; questionId?: string | null }
type BroadcastFn = (event: ActivityEvent, override?: Scope) => void

const ScopeContext = createContext<Scope>({})

// Wrap a subtree to give its descendants a default check period / question, so leaf
// components can call broadcast(event) with no arguments.
export function ActivityScope({
  checkPeriodId,
  questionId,
  children,
}: Scope & { children: React.ReactNode }) {
  return (
    <ScopeContext.Provider value={{ checkPeriodId, questionId }}>
      {children}
    </ScopeContext.Provider>
  )
}

export function useActivityBroadcast(): { broadcast: BroadcastFn; broadcastThrottled: BroadcastFn } {
  const { sendMessageToWebsocket } = useContext(GlobalContext)
  const scope = useContext(ScopeContext)

  // sendMessageToWebsocket is not memoised in App.tsx, so keep it in a ref to stop
  // broadcast()'s identity from churning on every parent render.
  const sendRef = useRef(sendMessageToWebsocket)
  useEffect(() => {
    sendRef.current = sendMessageToWebsocket
  })

  const lastSentRef = useRef<Record<string, number>>({})

  const broadcast = useCallback<BroadcastFn>(
    (event, override) => {
      const checkPeriodId = override?.checkPeriodId ?? scope.checkPeriodId
      const questionId = override?.questionId ?? scope.questionId
      if (!sendRef.current || !event || !checkPeriodId) return
      const message: Record<string, any> = { action: 'activityBroadcast', event, checkPeriodId }
      if (questionId) message.questionId = questionId
      try {
        sendRef.current(JSON.stringify(message))
      } catch {
        // best-effort
      }
    },
    [scope.checkPeriodId, scope.questionId]
  )

  // For events repeated while an action continues (typing, drawing, adjusting a map):
  // sends immediately, then at most once every 3s per event/scope.
  const broadcastThrottled = useCallback<BroadcastFn>(
    (event, override) => {
      const checkPeriodId = override?.checkPeriodId ?? scope.checkPeriodId
      const questionId = override?.questionId ?? scope.questionId
      const key = `${event}:${checkPeriodId}:${questionId ?? ''}`
      const now = Date.now()
      if (now - (lastSentRef.current[key] || 0) < 3000) return
      lastSentRef.current[key] = now
      broadcast(event, override)
    },
    [broadcast, scope.checkPeriodId, scope.questionId]
  )

  return { broadcast, broadcastThrottled }
}
